// SPDX-License-Identifier: AGPL-3.0-or-later

import {MESSAGE_USER_DESCRIPTOR} from '@app/features/channel/components/channel_textarea/shared';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import styles from '@app/features/user/components/profile/profile_card/ProfileMessageField.module.css';
import type {User} from '@app/features/user/models/User';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {SmileyIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';

const YOU_CAN_T_MESSAGE_YOURSELF_DESCRIPTOR = msg({
	message: "You can't message yourself",
	comment: 'Tooltip on the disabled message field of your own profile card.',
});

interface ProfileMessageFieldProps {
	user: User;
	isCurrentUser?: boolean;
	onClick?: () => void;
}

export const ProfileMessageField: React.FC<ProfileMessageFieldProps> = observer(({user, isCurrentUser, onClick}) => {
	const {i18n} = useLingui();
	const userName = NicknameUtils.formatTagForStreamerMode(user.tag).replace(/^@/, '');
	const field = (
		<button
			type="button"
			className={styles.field}
			disabled={isCurrentUser}
			onClick={onClick}
			data-flx="user.profile.profile-card.profile-message-field.field"
		>
			<span className={styles.text} data-flx="user.profile.profile-card.profile-message-field.text">
				{i18n._(MESSAGE_USER_DESCRIPTOR, {userName})}
			</span>
			<SmileyIcon
				aria-hidden={true}
				className={styles.icon}
				data-flx="user.profile.profile-card.profile-message-field.icon"
			/>
		</button>
	);
	if (!isCurrentUser) {
		return field;
	}
	return (
		<Tooltip
			text={i18n._(YOU_CAN_T_MESSAGE_YOURSELF_DESCRIPTOR)}
			maxWidth="xl"
			data-flx="user.profile.profile-card.profile-message-field.tooltip"
		>
			<div className={styles.wrap} data-flx="user.profile.profile-card.profile-message-field.wrap">
				{field}
			</div>
		</Tooltip>
	);
});
